import {useState} from 'react'
import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {requestFollow, cancelFollowRequest} from '#/lib/private/PrivateAccountService'
import {usePrivateProfileState} from '#/lib/private/privateHooks'
import {useAgent} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Lock_Stroke2_Corner0_Rounded as LockIcon} from '#/components/icons/Lock'
import {Text} from '#/components/Typography'

export function PrivateProfileGate({did}: {did: string}) {
  const t = useTheme()
  const {_} = useLingui()
  const agent = useAgent()
  const {state} = usePrivateProfileState({did, checkMembership: false})
  const [isWorking, setIsWorking] = useState(false)

  const requestState = state?.requestState ?? 'none'

  const onRequest = async () => {
    if (isWorking) return
    setIsWorking(true)
    try {
      await requestFollow(did, agent)
    } finally {
      setIsWorking(false)
    }
  }

  const onCancel = async () => {
    if (isWorking) return
    setIsWorking(true)
    try {
      await cancelFollowRequest(did, agent)
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <View style={[a.flex_1, a.align_center, a.gap_md, a.pt_3xl, a.px_lg]}>
      <View
        style={[
          a.align_center,
          a.justify_center,
          a.rounded_full,
          {
            width: 72,
            height: 72,
            backgroundColor: t.palette.contrast_50,
          },
        ]}>
        <LockIcon size={32} fill={t.palette.contrast_700} />
      </View>
      <Text style={[a.text_2xl, a.font_heavy, t.atoms.text]}>
        <Trans>This account is private</Trans>
      </Text>
      <Text
        style={[
          a.text_md,
          a.text_center,
          t.atoms.text_contrast_medium,
        ]}>
        <Trans>Follow requests must be approved to see posts and stories.</Trans>
      </Text>

      {requestState === 'requested' ? (
        <View style={[a.w_full, a.gap_sm]}>
          <Button
            color="secondary"
            variant="solid"
            size="large"
            disabled={isWorking}
            label={_(msg`Requested`)}
            style={[a.rounded_full]}>
            <ButtonText>
              <Trans>Requested</Trans>
            </ButtonText>
          </Button>
          <Button
            color="secondary"
            variant="ghost"
            size="large"
            disabled={isWorking}
            onPress={onCancel}
            label={_(msg`Cancel request`)}
            style={[a.rounded_full]}>
            <ButtonText>
              <Trans>Cancel request</Trans>
            </ButtonText>
          </Button>
        </View>
      ) : (
        <Button
          color="primary"
          variant="solid"
          size="large"
          disabled={isWorking}
          onPress={onRequest}
          label={_(msg`Request to follow`)}
          style={[a.rounded_full]}>
          <ButtonText>
            <Trans>Request to follow</Trans>
          </ButtonText>
        </Button>
      )}
    </View>
  )
}
